// Settings sections: their URL segment, sidebar entry and who sees them.

export type SettingsTab = 'assign' | 'customers' | 'people' | 'oemPartners' | 'inventory' | 'configuration' | 'users' | 'api' | 'system' | 'logs' | 'backup' | 'cleanup' | 'modelCatalog' | 'usbInstaller';

// Sidebar order. path is the /Settings/<path> segment.
export const SETTINGS_SECTIONS: { id: SettingsTab; path: string; icon: string; label: string }[] = [
  { id: 'assign', path: 'assign', icon: '🔗', label: 'Assign Radios' },
  { id: 'customers', path: 'customers', icon: '🧾', label: 'Customers' },
  { id: 'people', path: 'people', icon: '👤', label: 'People' },
  { id: 'oemPartners', path: 'oem-partners', icon: '🤝', label: 'OEM Partners' },
  { id: 'inventory', path: 'inventory', icon: '📦', label: 'Rental Inventory' },
  { id: 'configuration', path: 'config', icon: '⚙️', label: 'Configuration' },
  { id: 'users', path: 'users', icon: '🛡️', label: 'Platform Administrators' },
  { id: 'api', path: 'api', icon: '🔑', label: 'API Keys' },
  { id: 'system', path: 'system', icon: '📊', label: 'System' },
  { id: 'logs', path: 'logs', icon: '📋', label: 'Logs' },
  { id: 'backup', path: 'backup', icon: '💾', label: 'Backup & Restore' },
  { id: 'cleanup', path: 'cleanup', icon: '🧹', label: 'Cleanup & Retention' },
  { id: 'modelCatalog', path: 'models', icon: '📱', label: 'Model Catalog' },
  { id: 'usbInstaller', path: 'usb', icon: '🔌', label: 'Deployment' },
];

/** The section a /Settings/<segment> URL opens (undefined: unknown or retired). */
export function settingsTabFor(segment?: string): SettingsTab | undefined {
  return SETTINGS_SECTIONS.find((s) => s.path === segment)?.id;
}

// Retired sections and the section their bookmarks now open, matched case-insensitively like the
// router. The flat Network page is gone: networks are edited and deleted under their customer.
const RETIRED_SECTIONS: Record<string, string> = { network: 'customers' };

/** The /Settings/<segment> replacement of a retired section, or undefined. */
export function retiredSettingsSection(segment?: string): string | undefined {
  return segment ? RETIRED_SECTIONS[segment.toLowerCase()] : undefined;
}

// Which Settings sections a user sees. "customers" is a tenant-level screen and "people" is shown
// to everyone who administers people (delegated administration); every other section, including
// Configuration, is platform administration. This only hides navigation: the server decides
// every request regardless.
export function visibleSettingsTabs<T extends [string, unknown]>(tabs: T[], isPlatform: boolean, managesPeople = false): T[] {
  return tabs.filter(([id]) => isPlatform || id === 'customers' || (managesPeople && id === 'people'));
}
